import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Layers, User, ExternalLink } from "lucide-react";
import { PROJECTS, CATEGORIES } from "../../data";
import Reveal from "./Reveal";

const Projects = () => {
  const [filter, setFilter] = useState("Tous");
  const [selected, setSelected] = useState(null);

  const filtered = filter === "Tous" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="projets" className="py-24 lg:py-32" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400 mb-4">Travaux sélectionnés</p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-4xl lg:text-5xl mb-12">
            Projets qui laissent<br />une <span className="text-blue-500">empreinte</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-3 mb-12" data-testid="project-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-5 py-2 rounded-full text-sm font-mono uppercase tracking-wider border transition-colors duration-300 ${
                  filter === cat
                    ? "bg-blue-500 border-blue-500 text-white"
                    : "border-white/10 text-zinc-400 hover:border-blue-500/50 hover:text-white"
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map((project, i) => (
              <motion.article
                layout
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setSelected(project)}
                className="group cursor-pointer rounded-2xl border border-white/10 bg-[#111115] overflow-hidden hover:border-blue-500/40 transition-colors duration-500"
                data-testid={`project-card-${project.id}`}
              >
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#08080a]/80 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-[#08080a]/70 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
                    {project.category}
                  </span>
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Visiter ${project.title}`}
                    className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 hover:bg-blue-500 hover:text-white transition-all duration-300"
                    data-testid={`project-visit-${project.id}`}
                  >
                    <ArrowUpRight size={20} />
                  </a>
                </div>
                <div className="p-6 sm:p-8 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-semibold text-xl sm:text-2xl tracking-tight">{project.title}</h3>
                    <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{project.description}</p>
                  </div>
                  <span className="font-mono text-xs text-zinc-500 shrink-0">{project.year}</span>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelected(null)}
            data-testid="project-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 60, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#111115] border border-white/10"
              data-testid="project-modal"
            >
              <button
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-[#08080a]/80 backdrop-blur border border-white/10 flex items-center justify-center hover:bg-blue-500 transition-colors duration-300"
                data-testid="project-modal-close"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
              <img src={selected.image} alt={selected.title} className="w-full aspect-[16/8] object-cover" />
              <div className="p-6 sm:p-10">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400">{selected.category}</span>
                  <span className="text-zinc-600">·</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{selected.year}</span>
                </div>
                <h3 className="font-display font-bold text-3xl tracking-tight mb-4" data-testid="project-modal-title">
                  {selected.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-8">{selected.longDescription}</p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-sm text-zinc-300">
                    <Layers size={16} className="text-blue-500" /> {selected.duration}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-zinc-300">
                    <User size={16} className="text-blue-500" /> {selected.role}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-8" data-testid="project-modal-tech">
                  {selected.tech.map((t) => (
                    <span key={t} className="px-3 py-1.5 rounded-full text-xs font-mono border border-white/10 text-zinc-300">
                      {t}
                    </span>
                  ))}
                </div>

                <a
                  href={selected.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-black font-semibold text-sm px-6 py-3 rounded-full hover:bg-blue-500 hover:text-white transition-colors duration-300"
                  data-testid="project-modal-visit-link"
                >
                  <ExternalLink size={16} /> Visiter le site
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
